import { tint } from '../report-aggregation';
import { textOn } from './summary-boxes.component';

/**
 * The look the relationship ladder and the explainer panel share.
 *
 * The two face each other in a deck -- one explains how to read the other -- so every difference
 * between them reads as two different templates rather than as one report. They were written as two
 * stylesheets and drifted apart within a day: different heading sizes, different backgrounds, white
 * text on one and dark on the other. One stylesheet, used by both, is the only version of this that
 * stays true.
 *
 * What is *not* here is each module's own structure: the ladder is a table of figures and the
 * explainer a row of prose panels, and those have nothing to share.
 */

/**
 * The narrowest a stage is drawn on either module. Below it the stage heading wraps word by word and
 * the ladder's figures lose their bars, so a row too narrow for every stage at this width is widened
 * instead, and the deck scrolls sideways.
 *
 * Set so three equal stages fit in STAGE_ROW_MIN_WIDTH once the lead is taken off -- 237 comes to
 * 943px, a pixel over -- so the usual three-stage page does not scroll on a laptop at 100%. Four or
 * more stages still widen the row rather than squeezing.
 */
export const STAGE_MIN_WIDTH = 236;

/**
 * The narrowest the matrix and the explainer are drawn at all, whatever their stages: below it the
 * explainer's prose was too cramped on a laptop screen. Also the width a full-width module is given
 * at the narrowest the deck lays a subsection out (`$subsection-min-width` in the deck's stylesheet
 * is this plus a card's own padding), so at that point neither needs a scroll of its own.
 */
export const STAGE_ROW_MIN_WIDTH = 942;

/** The share of the card, in percent, spent before the first stage -- see --stage-lead below. */
export const STAGE_LEAD_PERCENT = 22;

/**
 * The width a row of stages needs: never under STAGE_ROW_MIN_WIDTH, and wider where its narrowest
 * stage would otherwise come out under STAGE_MIN_WIDTH.
 *
 * Every column is one width, so the stage spanning the fewest sets the width all of them need;
 * `ungrouped` are columns past the last stage, drawn at the same width. The lead before the first
 * stage is a share of the row rather than a width, so the columns and gutters are what is left
 * after it. One sum for both modules, and for the deck, which widens a module's column to fit it.
 */
export function stageRowMinWidth(spans: readonly number[], ungrouped: number, gutter: number): number {
  const drawn = spans.filter(span => span > 0);
  if (!drawn.length) return STAGE_ROW_MIN_WIDTH;
  const column = STAGE_MIN_WIDTH / Math.min(...drawn);
  const columns = drawn.reduce((total, span) => total + span, 0) + ungrouped;
  const gutters = (drawn.length - 1) * gutter;
  const staged = Math.ceil((columns * column + gutters) / (1 - STAGE_LEAD_PERCENT / 100));
  return Math.max(STAGE_ROW_MIN_WIDTH, staged);
}

/** The heading sizes are absolute, not em: an em in a table cell and an em in a div need not agree. */
export const STAGE_CARD_STYLES = `
  /* The corner, named once. The explainer rounds a real card and the ladder rounds the four cells
     that stand in for one, so the number is read from two stylesheets and must not be typed twice. */
  :host { --stage-radius: 8px; }
  .compact { --stage-radius: 6px; }

  /* A stage is a white card: a coloured heading, its parts under it, content below.

     Named stage-card and not card, which is Bootstrap's. A panel wearing that class was quietly
     picking up Bootstrap's 1px border, its display:flex, its word-wrap and its own radius, none of
     which the ladder's cells got -- so the two drifted apart through a stylesheet neither module
     contains. Nothing in either file could have shown that. */
  /* White. The block's own card is the wash for these two modules (see cardIsWashed), so a stage
     is a white card on it -- the same arrangement as every other module, one level in. */
  /* The run-in before the first stage begins.
   *
   * The ladder spends it on its row labels and its Total column; the explainer has nothing to put
   * there and simply indents by the same amount. Without it the two modules divide different widths
   * between the same stages, so the explainer teaches a shape the ladder does not draw -- which is
   * the one thing these two must not do.
   *
   * Shared here because it is a measurement both modules have to agree on, and neither owns it.
   */
  :host {
    --stage-lead: ${STAGE_LEAD_PERCENT}%;
    --stage-lead-label: 15%;
  }

  .stage-card {
    background: var(--ecr-card, #ffffff);
    border: 0;
    border-radius: var(--stage-radius);
    overflow: hidden;
  }

  /* !important throughout these four: projects/main/src/styles.scss sizes every h1-h6 with !important, which no selector can outrank without one of its own. The explainer panel draws the stage head as an h4 and
     the part head as an h5, so both were being resized out from under this file, while the ladder
     draws the same two as th and was not -- which is why the pair did not match. */
  .stage-head {
    margin: 0;
    padding: 20px 14px;
    font-size: 20px !important;
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
  }

  .part-head {
    margin: 0;
    padding: 12px 8px;
    font-size: 14px !important;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
  }

  .compact .stage-head { padding: 10px; font-size: 15px !important; }
  .compact .part-head { padding: 6px 4px; font-size: 11px !important; }

  /* Typed where it is drawn, and as it is drawn. */
  .inline {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 3px;
    padding: 0 3px;
    font: inherit;
    color: inherit;
    text-align: inherit;
    width: 100%;
  }
  .inline:focus { background: rgba(255, 255, 255, 0.15); outline: none; }

  /* The direction band. The gradient and the arrowhead shape are computed in spectrum-band.ts and
     bound inline, because they depend on how wide each caption is; what is here is how it sits.

     .spectrum is the row -- which the ladder also puts a spacer in, to start the arrow where its
     stages start -- and .arrow is the bar itself: one shape, one gradient, captions laid on it. */
  .spectrum {
    display: flex;
    margin-top: 18px;
  }
  .arrow {
    display: flex;
    /* Basis zero, so its width comes purely from the grow it is given. The ladder uses that to end
       the arrow where its stages end rather than at the edge of the card. */
    flex: 1 1 0;
    min-width: 0;
    /* Room for the heads to stand proud of the shaft. Must match SHAFT_INSET in spectrum-band.ts,
       which is where the shaft's own edges are cut: the captions then sit inside the shaft rather
       than across the flare. */
    padding: 7px 0;
  }
  .band {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 0;
    min-width: 0;
    padding: 13px 24px;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: var(--nav-true-white, #fff);
    text-transform: uppercase;
  }
  /* Clear of the arrowheads, which are 36px deep -- see HEAD in spectrum-band.ts. */
  .band:first-child { padding-left: 46px; }
  .band:last-child { padding-right: 46px; }

  /* The tools for a stage, at the foot of its card: add to it, move it along the row, drop it.
     Shared because the two modules are the same module -- one carries the figures and the other
     explains how to read them -- so a stage has to be built the same way in both. Sized in px for
     the reason the headings are: an em in a table cell and an em in a div need not agree. */
  .stage-tools {
    display: flex;
    align-items: center;
    padding: 8px 10px;
  }
  .tool, .drop {
    border: none;
    background: none;
    font-size: 13px;
    font-weight: 600;
    color: var(--nav-secondary);
  }
  .drop { color: var(--nav-error, #c0504d); }
  .tool:disabled, .drop:disabled { opacity: 0.35; }

  .add {
    border: 1px solid var(--nav-grey1, #f2f2f2);
    border-radius: 6px;
    background: none;
    font-size: 14px;
    font-weight: 600;
    color: var(--nav-secondary);
    padding: 5px 12px;
  }

  .band-label {
    display: block;
    margin: 14px 0 4px;
    font-size: 13px;
    font-weight: 600;
    color: var(--nav-secondary);
  }
  .band-input {
    width: 100%;
    border: 1px solid var(--nav-grey1, #f2f2f2);
    border-radius: 4px;
    padding: 6px 8px;
    font: inherit;
    font-size: 13px;
    resize: vertical;
  }
`;

/**
 * The text colour for a heading on a given fill.
 *
 * One rule for both modules, because a client palette that turns the explainer's headings dark while
 * the ladder's stay white is the same deck contradicting itself. Read off the fill rather than
 * assumed: a pale brand colour with white text on it is the one thing nobody can read.
 */
export function stageInk(fill: string): string {
  return textOn(fill);
}

/** How far the first sub-heading is lifted toward white from its stage's colour. */
const PART_TINT = 0.18;
/** And how much lighter each one after it, so a pair reads as first and second. */
const PART_STEP = 0.22;
/** The palest a sub-heading may get, so a five-part stage does not end in white. */
const PART_PALEST = 0.7;

/**
 * The fill for the `index`-th sub-heading of a stage.
 *
 * The two modules had a ramp each -- the explainer lifted its first part by {@link PART_TINT} and
 * the ladder left its first rung at the stage's own colour -- so the same heading on facing pages
 * came out two different shades. Worse than that: {@link stageInk} reads the fill to decide whether
 * the text on it is white or dark, so the two ramps crossed the readability threshold at different
 * points and the deck showed white text on one page and dark on the other for the same thing.
 *
 * One ramp is the only version of this that stays true, and it has to be the arithmetic rather than
 * the stylesheet, because the colour is computed from a palette that changes per client.
 */
export function stagePartFill(stageFill: string, index: number): string {
  return tint(stageFill, Math.min(PART_PALEST, PART_TINT + index * PART_STEP));
}
